// ═══════════════════════════════════════════════════════════════════════════
// urgenciaStock — Clasificación unificada de urgencia de stock
// ───────────────────────────────────────────────────────────────────────────
// Fuente única de verdad para clasificar publicaciones por su nivel de stock
// vs su tiempo de reposición (o un horizonte genérico). Se reusa entre:
//
//   - VelocidadConsumoDashboard      (origen histórico de la lógica)
//   - CentralPromocionesDashboard    (eje stock del evaluador de promos)
//   - Prospectiva / Planificación de Compras (T5 — badges de cobertura)
//   - VelocidadConsumoTnDashboard (lib velocidadConsumoTn, canal Tienda Nube)
//
// Vive en @core/lib (T3 Prospectiva, 3-ago-2026): la importaban clasificadores
// de core y ahora también Prospectiva — un vertical no importa de otro vertical
// ni core de un vertical (regla de los dos ejes).
//
// Modelo unificado:
//   Una sola variable N rige el clasificador:
//     - Modo Reposición → N = tiempoReposicionDias del producto.
//     - Modo Genérico   → N = diasCubrir (ventana configurable, default 30).
//
//   Umbrales (mismos multiplicadores en ambos modos):
//     Quebrado → stock = 0
//     Crítico  → días < N             (vas a quebrar antes de reponer)
//     Bajo     → días < N × 1.5       (margen apretado)
//     OK       → días < N × 2         (operación normal: stock entre 1N y 2N)
//     Sobrado  → días ≥ N × 2         (capital atrapado, ideal para promo)
//
//   Justificación de N×2: en operación cíclica (pedís cada N días lo que
//   vendés en N días), el stock oscila entre 1N (justo antes de recibir) y
//   2N (justo después). Más que 2N = sobrestock, no resiliencia.
// ═══════════════════════════════════════════════════════════════════════════

// ─── Diccionario de niveles de urgencia ─────────────────────────────────────
// Cada nivel incluye colores Tailwind para badges y un `rank` para ordenar.
// El `rank` mayor es "más sano" (más arriba en orden ascendente sería peor).

export const URGENCIA = Object.freeze({
  quebrado: {
    id: 'quebrado',
    label: 'Quebrado',
    emoji: '💀',
    color: '#991B1B',
    bg: 'bg-red-900/20',
    border: 'border-red-900/40',
    text: 'text-red-200',
    rank: 0,
  },
  critico: {
    id: 'critico',
    label: 'Crítico',
    emoji: '🔴',
    color: '#EF4444',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-300',
    rank: 1,
  },
  bajo: {
    id: 'bajo',
    label: 'Bajo',
    emoji: '🟠',
    color: '#F59E0B',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-300',
    rank: 2,
  },
  ok: {
    id: 'ok',
    label: 'OK',
    emoji: '🟢',
    color: '#22C55E',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-300',
    rank: 3,
  },
  sobrado: {
    id: 'sobrado',
    label: 'Sobrado',
    emoji: '🔵',
    color: '#3B82F6',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-300',
    rank: 4,
  },
})

// ─── Multiplicadores de los buckets ─────────────────────────────────────────
export const MULT_CRITICO = 1
export const MULT_BAJO    = 1.5
export const MULT_OK      = 2

// ─── Default cuando no hay tiempo de reposición ni selector cargado ────────
export const UMBRAL_OK_DEFAULT = 30

// Constantes históricas usadas como fallback en algunos renders. No las uses
// para clasificar — se exponen solo para callers viejos.
export const UMBRAL_CRITICO = MULT_CRITICO * UMBRAL_OK_DEFAULT // 30
export const UMBRAL_BAJO    = MULT_BAJO    * UMBRAL_OK_DEFAULT // 45
export const UMBRAL_OK      = MULT_OK      * UMBRAL_OK_DEFAULT // 60

/**
 * Calcula los umbrales (Crítico/Bajo/OK) para una fila en función del modo.
 * Devuelve además N (el horizonte aplicado) para que los consumidores puedan
 * derivar fórmulas de pedido sin recalcularlo.
 *
 * @param {Object} opts
 * @param {'generico'|'reposicion'} opts.modo
 * @param {number|null} opts.tiempoReposicion — días de reposición del producto.
 *                                              Solo se usa si modo='reposicion'.
 * @param {number} opts.diasCubrir — usado en modo genérico (default 30).
 *
 * @returns {{ N, uCrit, uBajo, uOk, base }}
 *   - N: horizonte aplicado en días.
 *   - uCrit/uBajo/uOk: días umbral para cada bucket.
 *   - base: 'reposicion' o 'generico' según cuál se usó.
 */
export function calcularUmbrales({ modo = 'generico', tiempoReposicion = null, diasCubrir = UMBRAL_OK_DEFAULT } = {}) {
  const repos = Number(tiempoReposicion) || 0
  // F8.6: 'mixto' se evalúa per-pub igual que 'reposicion' acá — si la pub
  // tiene tiempoReposicion lo usa, sino cae a genérico. La diferencia con
  // 'reposicion' es semántica para la UI (default cuando algunos productos
  // tienen tiempo de reposición y otros no).
  const usarReposicion = (modo === 'reposicion' || modo === 'mixto') && repos > 0
  const N = usarReposicion ? repos : (Number(diasCubrir) || UMBRAL_OK_DEFAULT)
  return {
    N,
    uCrit: N * MULT_CRITICO,
    uBajo: N * MULT_BAJO,
    uOk:   N * MULT_OK,
    base:  usarReposicion ? 'reposicion' : 'generico',
  }
}

/**
 * Clasifica la urgencia de una publicación según su stock y días de cobertura.
 *
 * @param {number} stock — unidades disponibles
 * @param {number|null} diasHastaQuiebre — cobertura proyectada en días
 *                                          (stock / velocidad). Puede ser null
 *                                          si no hay velocidad calculable.
 * @param {Object} opts — options para `calcularUmbrales`
 *
 * @returns {'quebrado'|'critico'|'bajo'|'ok'|'sobrado'}
 */
export function clasificarUrgencia(stock, diasHastaQuiebre, opts = {}) {
  if (!stock || stock <= 0) return 'quebrado'
  if (diasHastaQuiebre == null || !isFinite(diasHastaQuiebre)) return 'sobrado'
  const { uCrit, uBajo, uOk } = calcularUmbrales(opts)
  if (diasHastaQuiebre < uCrit) return 'critico'
  if (diasHastaQuiebre < uBajo) return 'bajo'
  if (diasHastaQuiebre < uOk)   return 'ok'
  return 'sobrado'
}

/**
 * Score numérico 0-100 derivado de la urgencia, para ordenamientos compuestos.
 * Sobrado=100 (ideal para promo), Crítico=10 (riesgo), Quebrado=0 (excluir).
 *
 * @param {string} urgencia — id de URGENCIA
 * @returns {number}
 */
export function scoreUrgencia(urgencia) {
  switch (urgencia) {
    case 'sobrado':  return 100
    case 'ok':       return 75
    case 'bajo':     return 40
    case 'critico':  return 10
    case 'quebrado': return 0
    default:         return 0
  }
}
